import React, { useEffect, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CompanyAddonDetail,
  getCompanyAddon,
  resolveOigChecksAddonId,
} from '@services/companyAddons';
import { getSelectedFacilityFromSession } from '@utils/facilityUtils';
import { facilitySessionId } from '@utils/billingFacilityUtils';
import { buildBillingAddonsUrl } from '@utils/billingAddonUrls';

const POLL_MS = 1500;
const MAX_ATTEMPTS = 20;
const SYNCING_COPY =
  'Still syncing your purchase. Refresh or open Exclusion Check in a minute.';

function firstPositiveAmount(value: number | string | null | undefined): number | undefined {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return undefined;
  return amount;
}

function formatCurrency(value: number | string | null | undefined): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatRenewalDate(value: string | null | undefined): string {
  if (!value) return 'Annual renewal';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Annual renewal';
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function detailRow(label: string, value: React.ReactNode) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}

export default function AddonPurchaseSuccess() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resolvedAddonId, setResolvedAddonId] = useState(searchParams.get('addon') || '');
  const [addon, setAddon] = useState<CompanyAddonDetail>();
  const [loading, setLoading] = useState(true);
  const [syncExhausted, setSyncExhausted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fromParams = searchParams.get('addon') || '';
    if (fromParams) {
      setResolvedAddonId(fromParams);
      return;
    }
    const facility = getSelectedFacilityFromSession<Record<string, unknown>>();
    resolveOigChecksAddonId(facilitySessionId(facility))
      .then((addonId) => {
        if (cancelled || !addonId) return;
        setResolvedAddonId(addonId);
        const next = new URLSearchParams(searchParams);
        next.set('tab', 'addons');
        next.set('addon', addonId);
        next.set('oigAddon', '1');
        setSearchParams(next, { replace: true });
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams]);

  const sessionIdParam = searchParams.get('session_id') || '';

  useEffect(() => {
    if (!resolvedAddonId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let attempts = 0;
    let timer: number | undefined;

    const load = async () => {
      const facility = getSelectedFacilityFromSession<Record<string, unknown>>();
      try {
        const detail = await getCompanyAddon(resolvedAddonId, facilitySessionId(facility));
        if (cancelled) return;
        setAddon(detail);
        const usage = detail?.usage || detail?.subscription?.usage;
        const hasPurchasedCapacity =
          detail?.subscription_state === 'active' &&
          usage != null &&
          Number(usage.total || 0) > 0;
        attempts += 1;
        if (!hasPurchasedCapacity && attempts < MAX_ATTEMPTS) {
          timer = window.setTimeout(load, POLL_MS);
          return;
        }
        if (!hasPurchasedCapacity) {
          setSyncExhausted(true);
        }
      } catch {
        if (!cancelled) {
          attempts += 1;
          if (attempts < MAX_ATTEMPTS) {
            timer = window.setTimeout(load, POLL_MS);
            return;
          }
          setSyncExhausted(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const start = async () => {
      setLoading(true);
      setSyncExhausted(false);
      if (!cancelled) {
        await load();
      }
    };

    start();
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [resolvedAddonId, sessionIdParam]);

  const usage = addon?.usage || addon?.subscription?.usage;
  const total = Number(usage?.total || 0);
  const used = Number(usage?.used || 0);
  const remaining = Number(usage?.remaining ?? Math.max(0, total - used));
  const tierLabel = addon?.current_tier_name || addon?.subscription?.tier_name;
  const chargedToday =
    firstPositiveAmount(addon?.aggregate?.prorated_charge_today) ??
    firstPositiveAmount(addon?.subscription?.tier_price);
  const renewalDate = formatRenewalDate(addon?.subscription?.renewal_date);
  const pendingBefore = Number(addon?.pending_request_count || 0);
  const usersEmailed =
    pendingBefore > 0
      ? 'Pending users will be notified as capacity becomes available'
      : 'None';

  const goToDetail = () => {
    navigate(buildBillingAddonsUrl(resolvedAddonId || undefined));
  };

  const capacityReady = Boolean(usage && total > 0);

  return (
    <Box
      sx={{ display: 'grid', gap: 2, maxWidth: 560, mx: 'auto', py: 4, px: 2 }}
      data-testid="addon-purchase-success"
    >
      <CheckCircleOutlineIcon
        sx={{
          fontSize: 56,
          color: capacityReady ? '#107c10' : syncExhausted ? '#8a6d00' : '#107c10',
          justifySelf: 'center',
        }}
        aria-hidden
      />
      <Typography variant="h5" sx={{ color: '#065b70', fontWeight: 600, textAlign: 'center' }}>
        {capacityReady
          ? 'Tier updated'
          : syncExhausted
            ? 'Payment received — capacity still syncing'
            : 'Confirming purchase…'}
      </Typography>
      <Typography sx={{ textAlign: 'center' }}>
        {capacityReady
          ? 'Your exclusion-check capacity is updated. Pending requests are auto-approved when applicable and requesters are notified.'
          : syncExhausted
            ? 'Stripe charged successfully, but pool capacity has not landed yet. Refresh later or open Exclusion Check. If this persists, webhook may have failed.'
            : 'Waiting for capacity to appear after payment…'}
      </Typography>

      {loading ? (
        <CircularProgress aria-label="Refreshing purchased capacity" size={28} sx={{ justifySelf: 'center' }} />
      ) : (
        <Box
          sx={{
            border: '1px solid #d8e8ec',
            borderRadius: 2,
            p: 2,
            backgroundColor: '#f8fdfe',
            display: 'grid',
            gap: 0.5,
          }}
          data-testid="addon-purchase-success-details"
        >
          {capacityReady ? (
            <>
              {detailRow('New pool total', `${total.toLocaleString()} checks${tierLabel ? ` (${tierLabel})` : ''}`)}
              {detailRow('Available now', `${remaining.toLocaleString()} checks`)}
            </>
          ) : (
            detailRow('New pool total', syncExhausted ? SYNCING_COPY : 'Updating…')
          )}
          {detailRow('Users emailed', usersEmailed)}
          {detailRow('Charged today', formatCurrency(chargedToday))}
          {detailRow('Next renewal', renewalDate)}
          {used > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {used.toLocaleString()} used · {remaining.toLocaleString()} remaining this month
            </Typography>
          ) : null}
        </Box>
      )}

      <Button variant="contained" onClick={goToDetail} sx={{ justifySelf: 'center', minWidth: 160 }}>
        Done
      </Button>
    </Box>
  );
}
